import {
  ButtonHTMLAttributes,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'

import type { LocalAudioTrack, LocalVideoTrack } from 'livekit-client'
import { computeMenuPosition, wasClickOutside } from '@livekit/components-core'
import { log } from '@livekit/components-core'

import { MediaDeviceSelect } from '@/src/components/controls/MediaDeviceSelect'
import { NoiseFilterSelect } from '@/src/components/controls/NoiseFilterSelect'
import { VirtualBgHandlerWrapper } from '@/src/components/controls/VirtualBgHandlerWrapper'

import { useClickEsc } from '@/src/hooks/useClickEsc.hook'

import { useAppSelector } from '@/src/store/hooks'
import { selectMediaDevicesState } from '@/src/store/slices/mediaDevices'
import { selectBranding } from '@/src/store/slices/meetData'

/** @public */
export interface MediaDeviceMenuProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  kind?: MediaDeviceKind
  initialSelection?: string
  enableVideoEffects?: boolean
  onActiveDeviceChange?: (kind: MediaDeviceKind, deviceId: string) => void
  tracks?: Partial<Record<MediaDeviceKind, LocalAudioTrack | LocalVideoTrack | undefined>>
  /**
   * this will call getUserMedia if the permissions are not yet given to enumerate the devices with device labels.
   * in some browsers multiple calls to getUserMedia result in multiple permission prompts.
   * It's generally advised only flip this to true, once a (preview) track has been acquired successfully with the
   * appropriate permissions.
   *
   * @see {@link PreJoin}
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/enumerateDevices | MDN enumerateDevices}
   */
  requestPermissions?: boolean
}

type TTitleOfKind = 'SelectMicrophone' | 'speakers' | 'selectCamera'

const getTitleOfKind = (kind: MediaDeviceKind) => {
  let title: TTitleOfKind = 'SelectMicrophone'

  switch (kind) {
    case 'audioinput':
      title = 'SelectMicrophone'
      break
    case 'audiooutput':
      title = 'speakers'
      break
    case 'videoinput':
      title = 'selectCamera'
      break
    default:
      break
  }

  return title
}

/**
 * The `MediaDeviceMenu` component is a button that opens a menu that lists
 * all media devices and allows the user to select them.
 *
 * @remarks
 * This component is implemented with the `MediaDeviceSelect` LiveKit components.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <MediaDeviceMenu />
 * </LiveKitRoom>
 * ```
 * @public
 */
export function MediaDeviceMenu({
  kind,
  initialSelection,
  enableVideoEffects = false,
  onActiveDeviceChange,
  tracks,
  requestPermissions = false,
  ...props
}: MediaDeviceMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [updateRequired, setUpdateRequired] = useState<boolean>(true)
  const [needPermissions, setNeedPermissions] = useState<boolean>(requestPermissions)

  const { showDevices } = useAppSelector(selectMediaDevicesState)[kind]
  const { text_primary_color } = useAppSelector(selectBranding)

  const { t } = useTranslation()

  const handleActiveDeviceChange = useCallback(
    (kind: MediaDeviceKind, deviceId: string) => {
      log.debug('handle device changed')
      onActiveDeviceChange?.(kind, deviceId)
    },
    [onActiveDeviceChange],
  )

  const handleActiveAudioOutputDeviceChange = useCallback(
    (deviceId: string) => {
      log.debug('handle audio output device changed. New audio device is ', deviceId)
      onActiveDeviceChange?.('audiooutput', deviceId)
    },
    [onActiveDeviceChange],
  )

  const button = useRef<HTMLButtonElement | null>(null)
  const tooltip = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    if (isOpen) {
      setNeedPermissions(true)
    }
  }, [isOpen])

  // TODO: Сейчас если MediaDeviceMenu не помещается в экран, то не появляется скролл. Нужно, чтобы появлялся
  useLayoutEffect(() => {
    let cleanup: ReturnType<typeof computeMenuPosition> | undefined
    if (button.current && tooltip.current && (devices || updateRequired)) {
      cleanup = computeMenuPosition(button.current, tooltip.current, (x, y) => {
        if (tooltip.current) {
          Object.assign(tooltip.current.style, { left: `${x}px`, top: `${y}px` })
        }
      })
    }
    setUpdateRequired(false)

    return () => {
      cleanup?.()
    }
  }, [button, tooltip, devices, updateRequired])

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (!tooltip.current || event.target === button.current) {
        return
      }

      if (isOpen && wasClickOutside(tooltip.current, event)) {
        setIsOpen(false)
      }
    },
    [isOpen, tooltip, button],
  )

  useClickEsc(isOpen, setIsOpen)

  useEffect(() => {
    const setUpdateIsRequire = () => setUpdateRequired(true)

    document.addEventListener<'click'>('click', handleClickOutside)
    window.addEventListener<'resize'>('resize', setUpdateIsRequire)

    return () => {
      document.removeEventListener<'click'>('click', handleClickOutside)
      window.removeEventListener<'resize'>('resize', setUpdateIsRequire)
    }
  }, [handleClickOutside])

  const customProps = useMemo(
    () => ({
      ...props,
      style: {
        ...props?.style,
        color: props?.style?.color ?? text_primary_color,
      },
    }),
    [props, text_primary_color],
  )

  return (
    <>
      <button
        {...customProps}
        aria-label="Device menu toggle button"
        className="lk-button lk-button-menu"
        aria-pressed={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        ref={button}
      >
        {props.children}
      </button>

      {/** only render when enabled in order to make sure that the permissions are requested only if the menu is enabled */}
      <div
        className="lk-device-menu"
        ref={tooltip}
        style={{ visibility: isOpen ? 'visible' : 'hidden', overflowY: 'auto' }}
      >
        <p style={{ color: `${text_primary_color}` }} className="lk-title-device-selection">
          {t(getTitleOfKind(kind))}
        </p>

        <MediaDeviceSelect
          initialSelection={initialSelection}
          onActiveDeviceChange={(deviceId) => handleActiveDeviceChange(kind, deviceId)}
          onDeviceListChange={setDevices}
          kind={kind}
          track={tracks?.[kind]}
          requestPermissions={needPermissions}
        />

        {kind === 'videoinput' && enableVideoEffects && (
          <div className="mobile-none">
            <VirtualBgHandlerWrapper />
          </div>
        )}

        {kind === 'audioinput' && showDevices && !isMobile && <p className="spacer mobile-none" />}

        {/* Добавляем также вывод динамиков в том же блоке, где выводятся выбор микрофона, поэтому здесь условие kind === 'audioinput' */}
        {kind === 'audioinput' && !isMobile && (
          <>
            <p style={{ color: `${text_primary_color}` }} className="lk-title-device-selection">
              {t('speakers')}
            </p>

            <MediaDeviceSelect
              kind="audiooutput"
              onActiveDeviceChange={handleActiveAudioOutputDeviceChange}
              onDeviceListChange={setDevices}
              track={tracks?.audiooutput}
              requestPermissions={needPermissions}
            />
          </>
        )}

        {kind === 'audioinput' && (
          <>
            {(showDevices || !isMobile) && <p className="spacer" />}
            <p style={{ color: `${text_primary_color}` }} className="lk-title-device-selection">
              {t('selectNoiseFilter')}
            </p>
            <NoiseFilterSelect />
          </>
        )}
      </div>
    </>
  )
}
